import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import { getUserBookings, deleteUserBooking } from '../services/userBookingService';
import { saveCalendarEvents } from '../services/calendarSheetService';

/**
 * User Booking Management Page
 *
 * Displays a data grid of user booking submissions (visit requests)
 * with filtering by phone number, sorting by created date,
 * detail modal on row click, delete button, and "Add to Calendar" button.
 */
const UserBookingPage = () => {
  const { t } = useTranslation();
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [addingToCalendar, setAddingToCalendar] = useState(null);
  const [calendarConfirm, setCalendarConfirm] = useState(null);

  // Load bookings from API only on mount or explicit refresh
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load all data without filter — filter/sort happens client-side
        // Always force refresh to get fresh data from the sheet (no cache)
        const data = await getUserBookings({ phone: '', sortOrder: 'desc', forceRefresh: true });
        setAllBookings(data);
      } catch (err) {
        console.error('[UserBookingPage] Error:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [refreshKey]);

  // Client-side filter by phone
  const filteredBookings = useMemo(() => {
    let result = allBookings;
    if (phoneFilter && phoneFilter.trim()) {
      const q = phoneFilter.trim().toLowerCase();
      result = result.filter((b) => b.phone.toLowerCase().includes(q));
    }
    // Client-side sort using parseVietnameseDate for proper comparison
    result = [...result].sort((a, b) => {
      const dateA = parseVietnameseDate(a.timestamp);
      const dateB = parseVietnameseDate(b.timestamp);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
    });
    return result;
  }, [allBookings, phoneFilter, sortOrder]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const showToast = (msg, type = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  /**
   * Parse a Vietnamese locale date string like "18/7/2026 10:39:55 SA" into a Date object.
   * SA = AM (sáng), CH = PM (chiều). Falls back to normal Date parsing if input is ISO-like.
   * Also handles formats without AM/PM or with different separators.
   */
  const parseVietnameseDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    let s = String(dateStr).trim();

    // Try normal Date parsing first (for ISO strings or standard formats)
    const normalDate = new Date(s);
    if (!isNaN(normalDate.getTime())) return normalDate;

    // Try Vietnamese locale format: dd/M/yyyy HH:mm:ss SA|CH
    // Also try without seconds: dd/M/yyyy HH:mm SA|CH
    const patterns = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(SA|CH)$/i,
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(SA|CH)$/i,
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/,
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/,
      // US format: M/d/yyyy HH:mm:ss
      /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2}):(\d{2})$/,
      /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})$/,
    ];

    for (const pattern of patterns) {
      const match = s.match(pattern);
      if (match) {
        let parts = match.slice(1);
        const hasAmPm = parts.length === 7;
        let [p1, p2, year, hours, minutes, seconds, ampm] = hasAmPm ? parts : [...parts, undefined];

        // Determine if format is dd/MM/yyyy (Vietnamese) or MM/dd/yyyy (US)
        // Vietnamese dates always have day first, and Apps Script uses vi-VN locale
        const day = p1;
        const month = p2;

        let h = parseInt(hours, 10);
        if (hasAmPm) {
          if (ampm.toUpperCase() === 'CH' && h !== 12) h += 12;
          if (ampm.toUpperCase() === 'SA' && h === 12) h = 0;
        }
        return new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
          h,
          parseInt(minutes, 10),
          seconds ? parseInt(seconds, 10) : 0
        );
      }
    }

    // Try cleaning up common formatting issues
    // Remove any trailing text after time, commas, etc.
    const cleaned = s.replace(/\s*SA$/i, '').replace(/\s*CH$/i, '').replace(/,\s*/g, ' ').trim();
    const fallbackDate = new Date(cleaned);
    if (!isNaN(fallbackDate.getTime())) return fallbackDate;

    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return dateStr || '—';
    try {
      const date = parseVietnameseDate(dateStr);
      if (!date || isNaN(date.getTime())) {
        // Debug: log the raw value to understand the format
        console.warn('[UserBookingPage] Unparseable timestamp:', JSON.stringify(dateStr));
        // Show the raw string as fallback instead of N/A
        return dateStr;
      }
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return dateStr || '—';
    try {
      const date = parseVietnameseDate(dateStr);
      if (!date || isNaN(date.getTime())) {
        console.warn('[UserBookingPage] Unparseable visitingDate:', JSON.stringify(dateStr));
        return dateStr;
      }
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const handleAddToCalendar = async (booking) => {
    setAddingToCalendar(booking.timestamp || booking.name);
    try {
      const eventId = 'booking_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
      const event = {
        id: eventId,
        userName: booking.name,
        phone: booking.phone,
        propertyName: booking.propertyName,
        propertyId: booking.propertyId || booking.drivePropertyId || '',
        district: booking.district || '',
        date: booking.visitingDate,
        notes: booking.note || '',
        propertyLink: booking.propertyUrl,
        isRevisit: false,
        revisitOfId: '',
      };

      // Save to CalendarEvents sheet via the proxy API
      await saveCalendarEvents([event]);

      showToast(`Đã thêm lịch hẹn của ${booking.name} vào lịch xem nhà thành công!`);
    } catch (err) {
      console.error('[UserBookingPage] Calendar error:', err);
      showToast('Không thể thêm vào lịch', 'error');
    } finally {
      setAddingToCalendar(null);
    }
  };

  const handleDelete = async (booking) => {
    const deleteKey = booking.epochTimestamp || booking.timestamp;
    if (!deleteKey || deleteKey === 'N/A') {
      showToast('Không thể xoá: thiếu thông tin định danh', 'error');
      return;
    }
    setDeleting(true);
    try {
      await deleteUserBooking(deleteKey);
      showToast(`Đã xoá đăng ký của ${booking.name} thành công!`);
      setDeleteConfirm(null);
      setSelectedBooking(null);
      handleRefresh();
    } catch (err) {
      console.error('[UserBookingPage] Delete error:', err);
      showToast(err.message || 'Không thể xoá đăng ký', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setSelectedBooking(null);
        setDeleteConfirm(null);
        setCalendarConfirm(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Loading state
  if (loading && allBookings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-96 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <AdminBreadcrumb />

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quản lý đặt lịch xem nhà</h1>
              <p className="text-gray-600 mt-2">
                Tổng số: {filteredBookings.length} đăng ký
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                title="Làm mới"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {loading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-primary-600">
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Đang tải dữ liệu...</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg mb-6 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tìm kiếm theo số điện thoại
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                  placeholder="Nhập số điện thoại để tìm kiếm..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sắp xếp theo ngày
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="desc">Mới nhất trước</option>
                <option value="asc">Cũ nhất trước</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Grid */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {error ? (
            <div className="p-12 text-center">
              <div className="text-red-600 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-lg text-gray-900 font-medium mb-2">Lỗi tải dữ liệu</p>
              <p className="text-gray-600 mb-4">{error}</p>
              <button onClick={handleRefresh} className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                Thử lại
              </button>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg">Chưa có đăng ký xem nhà nào</p>
              {phoneFilter && (
                <p className="text-sm text-gray-400 mt-2">Không tìm thấy kết quả với số điện thoại "{phoneFilter}"</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Họ tên</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số điện thoại</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ngày xem nhà</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tên nhà trọ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ghi chú</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ngày đăng ký</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.map((booking, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedBooking(booking)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{booking.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <a href={`tel:${booking.phone}`} className="text-primary-600 hover:text-primary-700 hover:underline">
                          {booking.phone}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(booking.visitingDate)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-[200px] truncate">
                        {booking.propertyName && booking.propertyName !== 'N/A' ? (
                          booking.propertyUrl ? (
                            <a href={booking.propertyUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 hover:underline">
                              {booking.propertyName}
                            </a>
                          ) : booking.propertyName
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-[200px] truncate">
                        {booking.note || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(booking.timestamp)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setCalendarConfirm(booking)}
                          disabled={addingToCalendar !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors text-sm font-medium mr-2 disabled:opacity-60 disabled:cursor-not-allowed"
                          title="Thêm vào lịch xem nhà"
                        >
                          {addingToCalendar === (booking.timestamp || booking.name) ? (
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          {addingToCalendar === (booking.timestamp || booking.name) ? 'Đang thêm...' : 'Thêm lịch'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(booking)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                          title="Xoá đăng ký"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Xoá
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedBooking && !deleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setSelectedBooking(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Chi tiết đăng ký xem nhà</h3>
                  <p className="text-xs text-gray-500">{formatDate(selectedBooking.timestamp)}</p>
                </div>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Họ tên</p>
                  <p className="text-base font-semibold text-gray-900">{selectedBooking.name}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Số điện thoại</p>
                  <a href={`tel:${selectedBooking.phone}`} className="text-base font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                    {selectedBooking.phone}
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Ngày & giờ xem nhà</p>
                <p className="text-base font-medium text-gray-900">{formatDateOnly(selectedBooking.visitingDate)}</p>
              </div>

              {selectedBooking.propertyName && selectedBooking.propertyName !== 'N/A' && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Nhà trọ</p>
                  {selectedBooking.propertyUrl ? (
                    <a href={selectedBooking.propertyUrl} target="_blank" rel="noopener noreferrer" className="text-base font-medium text-primary-600 hover:text-primary-700 hover:underline">
                      {selectedBooking.propertyName}
                    </a>
                  ) : (
                    <p className="text-base font-medium text-gray-900">{selectedBooking.propertyName}</p>
                  )}
                </div>
              )}

              {selectedBooking.note && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-1">Ghi chú</p>
                      <p className="text-sm text-amber-900 whitespace-pre-wrap">{selectedBooking.note}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteConfirm(selectedBooking);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Xoá đăng ký
              </button>
              <button
                onClick={() => {
                  setCalendarConfirm(selectedBooking);
                }}
                disabled={addingToCalendar !== null}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {addingToCalendar !== null ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                {addingToCalendar !== null ? 'Đang thêm...' : 'Thêm vào lịch'}
              </button>
              <button onClick={() => setSelectedBooking(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Calendar Confirmation Modal */}
      {calendarConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setCalendarConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Xác nhận thêm vào lịch</h3>
              <p className="text-gray-600 mb-2">
                Bạn có chắc chắn muốn thêm lịch hẹn của <strong>{calendarConfirm.name}</strong> vào lịch xem nhà?
              </p>
              <p className="text-sm text-gray-500 mb-6">Thông tin sẽ được lưu vào Google Sheet CalendarEvents.</p>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setCalendarConfirm(null)}
                  disabled={addingToCalendar !== null}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Huỷ
                </button>
                <button
                  onClick={() => {
                    const booking = calendarConfirm;
                    setCalendarConfirm(null);
                    handleAddToCalendar(booking);
                  }}
                  disabled={addingToCalendar !== null}
                  className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {addingToCalendar !== null ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Đang thêm...
                    </>
                  ) : 'Xác nhận thêm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Xác nhận xoá</h3>
              <p className="text-gray-600 mb-2">
                Bạn có chắc chắn muốn xoá đăng ký của <strong>{deleteConfirm.name}</strong>?
              </p>
              <p className="text-sm text-gray-500 mb-6">Hành động này sẽ xoá dữ liệu khỏi Google Sheet và không thể hoàn tác.</p>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Huỷ
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleting}
                  className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Đang xoá...
                    </>
                  ) : 'Xác nhận xoá'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999]">
          <div className={`px-6 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center space-x-2 whitespace-nowrap ${
            toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {toastType === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            <span>{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBookingPage;